import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { MutableRefObject } from 'react';

type UseIdleTimerArgs = {
  enabled: boolean;
  idleTimeoutMs: number;
  warningDurationMs: number;
  onTimeout: () => void;
};

type UseIdleTimerResult = {
  isWarningOpen: boolean;
  warningSecondsRemaining: number;
  continueSession: () => void;
};

const ACTIVITY_EVENTS: Array<keyof WindowEventMap> = [
  'mousemove',
  'mousedown',
  'keydown',
  'scroll',
  'touchstart',
];

/** Ignore trackpad/DevTools micro-jitter that would otherwise reset idle forever. */
const MOUSE_MOVE_THRESHOLD_PX = 10;

const ACTIVITY_THROTTLE_MS = 500;

function isMeaningfulActivity(
  event: Event,
  lastMousePositionRef: MutableRefObject<{ x: number; y: number } | null>
): boolean {
  if (event.type !== 'mousemove') {
    return true;
  }

  if (!(event instanceof MouseEvent)) {
    return false;
  }

  const reportedMovement = Math.abs(event.movementX) + Math.abs(event.movementY);
  if (reportedMovement >= MOUSE_MOVE_THRESHOLD_PX) {
    lastMousePositionRef.current = { x: event.clientX, y: event.clientY };
    return true;
  }

  // Some browsers report 0 movementX/Y; fall back to client coordinate delta.
  const lastPosition = lastMousePositionRef.current;
  lastMousePositionRef.current = { x: event.clientX, y: event.clientY };
  if (lastPosition == null) {
    return false;
  }

  const deltaX = event.clientX - lastPosition.x;
  const deltaY = event.clientY - lastPosition.y;
  return Math.hypot(deltaX, deltaY) >= MOUSE_MOVE_THRESHOLD_PX;
}

export function useIdleTimer({
  enabled,
  idleTimeoutMs,
  warningDurationMs,
  onTimeout,
}: UseIdleTimerArgs): UseIdleTimerResult {
  const warningTimeoutRef = useRef<number | null>(null);
  const expireTimeoutRef = useRef<number | null>(null);
  const countdownIntervalRef = useRef<number | null>(null);
  const warningStartedAtRef = useRef<number | null>(null);
  const lastActivityRef = useRef(0);
  const lastMousePositionRef = useRef<{ x: number; y: number } | null>(null);
  const isWarningOpenRef = useRef(false);
  const onTimeoutRef = useRef(onTimeout);
  onTimeoutRef.current = onTimeout;

  const [isWarningOpen, setIsWarningOpen] = useState(false);
  const [warningMsRemaining, setWarningMsRemaining] = useState(warningDurationMs);
  isWarningOpenRef.current = isWarningOpen;

  const clearTimerRef = (timerRef: MutableRefObject<number | null>) => {
    if (timerRef.current == null) {
      return;
    }

    window.clearTimeout(timerRef.current);
    timerRef.current = null;
  };

  const clearIntervalRef = (timerRef: MutableRefObject<number | null>) => {
    if (timerRef.current == null) {
      return;
    }

    window.clearInterval(timerRef.current);
    timerRef.current = null;
  };

  const clearAllTimers = useCallback(() => {
    clearTimerRef(warningTimeoutRef);
    clearTimerRef(expireTimeoutRef);
    clearIntervalRef(countdownIntervalRef);
    warningStartedAtRef.current = null;
  }, []);

  const stopWarningCountdown = useCallback(() => {
    clearIntervalRef(countdownIntervalRef);
    warningStartedAtRef.current = null;
    setWarningMsRemaining(warningDurationMs);
  }, [warningDurationMs]);

  const startWarningCountdown = useCallback(() => {
    warningStartedAtRef.current = Date.now();
    setWarningMsRemaining(warningDurationMs);
    clearIntervalRef(countdownIntervalRef);

    countdownIntervalRef.current = window.setInterval(() => {
      if (warningStartedAtRef.current == null) {
        return;
      }

      const elapsed = Date.now() - warningStartedAtRef.current;
      const remaining = Math.max(0, warningDurationMs - elapsed);
      setWarningMsRemaining(remaining);
    }, 250);
  }, [warningDurationMs]);

  const scheduleTimers = useCallback(() => {
    clearAllTimers();
    setIsWarningOpen(false);
    setWarningMsRemaining(warningDurationMs);

    const warningDelayMs = idleTimeoutMs - warningDurationMs;
    warningTimeoutRef.current = window.setTimeout(() => {
      setIsWarningOpen(true);
      startWarningCountdown();
    }, warningDelayMs);

    expireTimeoutRef.current = window.setTimeout(() => {
      onTimeoutRef.current();
    }, idleTimeoutMs);
  }, [clearAllTimers, idleTimeoutMs, startWarningCountdown, warningDurationMs]);

  const scheduleTimersRef = useRef(scheduleTimers);
  scheduleTimersRef.current = scheduleTimers;

  const continueSession = useCallback(() => {
    if (!enabled) {
      return;
    }

    stopWarningCountdown();
    scheduleTimers();
  }, [enabled, scheduleTimers, stopWarningCountdown]);

  useEffect(() => {
    if (!enabled) {
      clearAllTimers();
      setIsWarningOpen(false);
      setWarningMsRemaining(warningDurationMs);
      lastMousePositionRef.current = null;
    } else {
      scheduleTimers();
    }

    return () => {
      clearAllTimers();
    };
  }, [clearAllTimers, enabled, scheduleTimers, warningDurationMs]);

  useEffect(() => {
    if (!enabled) {
      return () => undefined;
    }

    const handleActivity = (event: Event) => {
      if (isWarningOpenRef.current) {
        return;
      }

      if (!isMeaningfulActivity(event, lastMousePositionRef)) {
        return;
      }

      const now = Date.now();
      // Throttle high-frequency input to avoid recreating timers constantly.
      if (now - lastActivityRef.current < ACTIVITY_THROTTLE_MS) {
        return;
      }

      lastActivityRef.current = now;
      scheduleTimersRef.current();
    };

    ACTIVITY_EVENTS.forEach(eventName => {
      window.addEventListener(eventName, handleActivity, { passive: true });
    });

    return () => {
      ACTIVITY_EVENTS.forEach(eventName => {
        window.removeEventListener(eventName, handleActivity);
      });
    };
    // Attach once per enabled session; refs keep handlers current (avoids remount thrash).
  }, [enabled]);

  const warningSecondsRemaining = useMemo(
    () => Math.max(1, Math.ceil(warningMsRemaining / 1000)),
    [warningMsRemaining]
  );

  return {
    isWarningOpen,
    warningSecondsRemaining,
    continueSession,
  };
}
